import { useCallback, useEffect, useRef, useState } from 'react';

export interface Resource<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => Promise<void>;
  refresh: () => Promise<void>;
  setData: (value: T) => void;
}

export function useResource<T>(fetcher: () => Promise<T>, deps: unknown[]): Resource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async (silent: boolean) => {
    const current = ++generation.current;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const value = await fetcherRef.current();
      if (current !== generation.current) return;
      setData(value);
      setError(null);
    } catch (err) {
      if (current !== generation.current) return;
      if (!silent) setError(err);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setData(undefined);
    void run(false);
  }, deps);

  const reload = useCallback(() => run(false), [run]);
  const refresh = useCallback(() => run(true), [run]);

  return { data, error, loading, reload, refresh, setData };
}

export function usePolling(active: boolean, callback: () => void, intervalMs = 4000): void {
  const ref = useRef(callback);
  ref.current = callback;
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => ref.current(), intervalMs);
    return () => window.clearInterval(timer);
  }, [active, intervalMs]);
}

export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
